import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../axiosConfig';
import './NewPostModal.css';

const STATE_CODES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY'
];

const NewPostModal = ({ visible, onClose, onPostCreated }) => {
  const [postType, setPostType] = useState('club');
  const [selectedClub, setSelectedClub] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [clubSearch, setClubSearch] = useState('');
  const [loadingClubs, setLoadingClubs] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Fetch clubs when switching to club tab
  useEffect(() => {
    if (postType === 'club' && clubs.length === 0) {
      fetchClubs();
    }
  }, [postType]);

  const fetchClubs = async () => {
    setLoadingClubs(true);
    try {
      const res = await axiosInstance.get('/search-clubs/');
      let data = res.data;
      if (data?.clubs) data = data.clubs;
      else if (data?.results) data = data.results;
      else if (!Array.isArray(data)) data = [];

      const standardized = (Array.isArray(data) ? data : []).map(c => ({
        club_id: c.club_id || c.id,
        name: c.name || c.golf_course || 'Unknown',
        city: c.city || '',
        state: c.state || '',
        access_type: c.access_type || 'private',
      })).filter(c => c.access_type?.toLowerCase() === 'private');

      setClubs(standardized);
    } catch (e) {
      console.error('Error fetching clubs:', e);
    } finally {
      setLoadingClubs(false);
    }
  };

  const filteredClubs = clubs.filter(c => {
    if (!clubSearch) return true;
    const s = clubSearch.toLowerCase();
    return c.name.toLowerCase().includes(s) || c.city.toLowerCase().includes(s) || c.state.toLowerCase().includes(s);
  }).slice(0, 8);

  const resetForm = () => {
    setPostType('club');
    setSelectedClub(null);
    setSelectedState(null);
    setTitle('');
    setContent('');
    setImages([]);
    setVideo(null);
    setLinkUrl('');
    setShowLinkInput(false);
    setClubSearch('');
    setError('');
    setUploadProgress(0);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    const remaining = 4 - images.length;
    const newImages = files.slice(0, remaining).map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name,
    }));
    setImages(prev => [...prev, ...newImages]);
    setVideo(null);
    e.target.value = '';
  };

  const handleVideoSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      setError('Video must be smaller than 50MB');
      return;
    }
    setVideo({ file, preview: URL.createObjectURL(file), name: file.name });
    setImages([]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };

  const canSubmit = title.trim() &&
    (content.trim() || images.length > 0 || video) &&
    (postType === 'club' ? !!selectedClub : !!selectedState) &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);

    const formData = new FormData();
    formData.append('title', title.trim());
    if (content.trim()) formData.append('content', content.trim());
    if (linkUrl.trim()) formData.append('link_url', linkUrl.trim());

    images.forEach((img) => {
      formData.append('images', img.file);
    });

    if (video) {
      formData.append('video', video.file);
    }

    let endpoint;
    if (postType === 'club') {
      formData.append('club_id', selectedClub.club_id);
      endpoint = '/messageboard/messages/';
    } else {
      endpoint = selectedState === 'General'
        ? '/messageboard/forums/general/'
        : `/messageboard/forums/${selectedState}/`;
    }

    try {
      await axiosInstance.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      handleClose();
      if (onPostCreated) onPostCreated();
    } catch (e) {
      console.error('Error creating post:', e);
      setError(e.response?.data?.error || 'Failed to create post. Please try again.');
    } finally {
      setSubmitting(false);
      setUploadProgress(0);
    }
  };

  if (!visible) return null;

  return (
    <div className="npm-overlay" onClick={handleClose}>
      <div className="npm-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="npm-header">
          <h2 className="npm-title">New Post</h2>
          <button className="npm-close" onClick={handleClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        {/* Type Toggle */}
        <div className="npm-toggle">
          <button className={`npm-toggle-btn ${postType === 'club' ? 'active' : ''}`} onClick={() => { setPostType('club'); setSelectedState(null); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/></svg>
            Club
          </button>
          <button className={`npm-toggle-btn ${postType === 'forum' ? 'active' : ''}`} onClick={() => { setPostType('forum'); setSelectedClub(null); }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
            Forum
          </button>
        </div>

        {/* Club Selector */}
        {postType === 'club' && (
          <div className="npm-selector">
            <label className="npm-selector-label">
              {selectedClub ? selectedClub.name : 'Choose a club'}
            </label>
            <input
              type="text"
              className="npm-search"
              placeholder="Search clubs..."
              value={clubSearch}
              onChange={(e) => setClubSearch(e.target.value)}
            />
            <div className="npm-chips">
              {loadingClubs ? (
                <span className="npm-chips-loading">Loading clubs...</span>
              ) : filteredClubs.length === 0 ? (
                <span className="npm-chips-empty">No clubs found</span>
              ) : (
                filteredClubs.map(club => (
                  <button
                    key={club.club_id}
                    className={`npm-chip ${selectedClub?.club_id === club.club_id ? 'active' : ''}`}
                    onClick={() => setSelectedClub(club)}
                  >
                    <span className="npm-chip-name">{club.name}</span>
                    <span className="npm-chip-loc">{club.city}, {club.state}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        {/* Forum/State Selector */}
        {postType === 'forum' && (
          <div className="npm-selector">
            <label className="npm-selector-label">
              {selectedState ? (selectedState === 'General' ? 'USA Forum' : `${selectedState} Forum`) : 'Choose a forum'}
            </label>
            <div className="npm-chips npm-chips-scroll">
              <button
                className={`npm-chip ${selectedState === 'General' ? 'active' : ''}`}
                onClick={() => setSelectedState('General')}
              >
                USA
              </button>
              {STATE_CODES.map(s => (
                <button
                  key={s}
                  className={`npm-chip ${selectedState === s ? 'active' : ''}`}
                  onClick={() => setSelectedState(s)}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Form Body */}
        <div className="npm-form">
          <input
            type="text"
            className="npm-input-title"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
          />

          {/* Media Toolbar */}
          <div className="npm-toolbar">
            <button className="npm-toolbar-btn" onClick={() => fileInputRef.current?.click()} disabled={images.length >= 4 || !!video}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
              {images.length > 0 && <span className="npm-toolbar-count">{images.length}</span>}
            </button>
            <button className="npm-toolbar-btn" onClick={() => videoInputRef.current?.click()} disabled={images.length > 0 || !!video}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            </button>
            <button className={`npm-toolbar-btn ${showLinkInput ? 'active' : ''}`} onClick={() => setShowLinkInput(!showLinkInput)}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
            </button>
            <span className="npm-toolbar-info">
              {images.length > 0 ? `${images.length} image${images.length > 1 ? 's' : ''}` : video ? '1 video' : 'Add media'}
            </span>
          </div>

          <input type="file" ref={fileInputRef} accept="image/*" multiple onChange={handleImageSelect} style={{ display: 'none' }} />
          <input type="file" ref={videoInputRef} accept="video/*" onChange={handleVideoSelect} style={{ display: 'none' }} />

          {/* Link Input */}
          {showLinkInput && (
            <div className="npm-link-input">
              <input
                type="url"
                placeholder="https://example.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
              />
              <button onClick={() => { setShowLinkInput(false); setLinkUrl(''); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
          )}

          {/* Media Previews */}
          {images.length > 0 && (
            <div className="npm-previews">
              {images.map((img, i) => (
                <div key={i} className="npm-preview-item">
                  <img src={img.preview} alt={`Upload ${i + 1}`} />
                  <button className="npm-preview-remove" onClick={() => removeImage(i)}>×</button>
                </div>
              ))}
            </div>
          )}

          {video && (
            <div className="npm-video-preview">
              <span>{video.name}</span>
              <button onClick={() => setVideo(null)}>×</button>
            </div>
          )}

          <textarea
            className="npm-input-content"
            placeholder="What's on your mind?"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            maxLength={2000}
            rows={4}
          />
          <div className="npm-char-count">{content.length}/2000</div>
        </div>

        {/* Error */}
        {error && <div className="npm-error">{error}</div>}

        {/* Upload Progress */}
        {submitting && uploadProgress > 0 && (
          <div className="npm-progress">
            <div className="npm-progress-bar" style={{ width: `${uploadProgress}%` }} />
          </div>
        )}

        {/* Footer */}
        <div className="npm-footer">
          <button className="npm-cancel" onClick={handleClose}>Cancel</button>
          <button className="npm-submit" onClick={handleSubmit} disabled={!canSubmit}>
            {submitting ? 'Posting...' : 'Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewPostModal;
